import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { IProfessional } from '../../shared/interfaces';
import { Card, Col, Empty, Flex, Row, Space, Tag, Typography } from 'antd';
import {
  ATable,
  MenuOptions,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { LiaUserNurseSolid } from 'react-icons/lia';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  TeamOutlined,
  UserOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { getProfessionals } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Professionals: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const [search, setSearch] = useState<string>('');

  const summary = useMemo(
    () => ({
      total: professionals.length,
      active: professionals.filter((p) => p.status).length,
      inactive: professionals.filter((p) => !p.status).length,
    }),
    [professionals],
  );

  const listProfessionals = useCallback(async () => {
    setLoading(true);
    setProfessionals([]);
    try {
      const response = await getProfessionals(
        limit,
        (page - 1) * limit,
        search,
      );
      setPages(response.count);
      setProfessionals(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar profissionais',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  useEffect(() => {
    listProfessionals();
  }, [page, search, limit, listProfessionals]);

  return (
    <PageLayout>
      <PageIntro
        badge="Profissionais"
        icon={<LiaUserNurseSolid />}
        title="Profissionais"
        description="Acompanhe o quadro da clínica, filtre rapidamente por nome e acesse a edição dos cadastros com um visual mais limpo."
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Flex align="center" gap={12}>
              <TeamOutlined style={{ fontSize: 24, color: '#1677ff' }} />
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Total de Profissionais
                </Typography.Text>
                <div>
                  <Typography.Text strong style={{ fontSize: 20 }}>
                    {summary.total}
                  </Typography.Text>
                </div>
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Flex align="center" gap={12}>
              <UserOutlined style={{ fontSize: 24, color: '#52c41a' }} />
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Ativos
                </Typography.Text>
                <div>
                  <Typography.Text
                    strong
                    style={{ fontSize: 20, color: '#52c41a' }}
                  >
                    {summary.active}
                  </Typography.Text>
                </div>
              </div>
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Flex align="center" gap={12}>
              <StopOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Inativos
                </Typography.Text>
                <div>
                  <Typography.Text
                    strong
                    style={{ fontSize: 20, color: '#ff4d4f' }}
                  >
                    {summary.inactive}
                  </Typography.Text>
                </div>
              </div>
            </Flex>
          </Card>
        </Col>
      </Row>

      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listProfessionals(),
          },
          permissionFind([PermissionActions.professionalsCreate])
            ? {
                label: 'Novo Profissional',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => navigate(`/professionals/new`),
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />

      <PagePanel>
        <ATable
          style={{ marginTop: 10 }}
          scroll={{ y: 510 }}
          columns={[
            {
              title: '#',
              width: 60,
              render: (_v, _r, index) => (
                <Typography.Text type="secondary">{index + 1}</Typography.Text>
              ),
            },
            {
              title: 'CPF',
              dataIndex: 'cpf',
              width: 140,
            },
            {
              title: 'Nome',
              dataIndex: 'name',
              width: '45%',
              render: (value: string) => (
                <Space>
                  <UserOutlined style={{ color: '#1677ff' }} />
                  <Typography.Text strong>{value}</Typography.Text>
                </Space>
              ),
            },
            {
              title: 'Especialidade',
              dataIndex: 'specialty_description',
              width: 180,
              render: (value: string) =>
                value ? (
                  <Tag color="blue">{value}</Tag>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 100,
              render: (value: boolean) =>
                value ? (
                  <Tag color="success">Ativo</Tag>
                ) : (
                  <Tag color="error">Inativo</Tag>
                ),
            },
            {
              title: 'Opções',
              width: 100,
              render: (cell: IProfessional) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.professionalsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => navigate(`/professionals/${cell.id}`),
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhum profissional encontrado"
              />
            ),
          }}
          loading={loading}
          dataSource={professionals}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default Professionals;
